import React, {useState, useEffect} from 'react'
import {Servicio} from '../core/models'
import {KTIcon} from '../../../../_metronic/helpers'

interface Props {
  onSubmit: (servicio: Omit<Servicio, 'id'>) => void
  loading?: boolean
  initialData?: Servicio
  isEditMode?: boolean
}

export const CreateServicioForm: React.FC<Props> = ({
  onSubmit, 
  loading = false, 
  initialData,
  isEditMode = false
}) => {
  const [formData, setFormData] = useState<Omit<Servicio, 'id'>>({
    nombre: '',
    valor: '0',
    descripcion: '',
    estado: true,
    variable: false,
    recurrente: false,
    exonerado: false,
    enviar_facturacion_electronica: false,
    codigo_contable: '',
  })

  // Cargar datos iniciales si está en modo edición
  useEffect(() => {
    if (initialData) {
      setFormData({
        nombre: initialData.nombre,
        valor: initialData.valor,
        descripcion: initialData.descripcion,
        estado: initialData.estado,
        variable: initialData.variable,
        recurrente: initialData.recurrente,
        exonerado: initialData.exonerado ?? false,
        enviar_facturacion_electronica: initialData.enviar_facturacion_electronica ?? false,
        codigo_contable: initialData.codigo_contable ?? '',
      })
    }
  }, [initialData])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {name, value, type} = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <div className='card'>
      <div className='card-header'>
        <h3 className='card-title'>
          {isEditMode ? 'Editar Servicio' : 'Crear Nuevo Servicio'}
        </h3>
      </div>
      <div className='card-body'>
        <form onSubmit={handleSubmit}>
          <div className='row mb-5'>
            <div className='col-md-6'>
              <label className='form-label required'>Nombre</label>
              <input
                type='text'
                className='form-control'
                name='nombre'
                value={formData.nombre}
                onChange={handleChange}
                required
              />
            </div>
            <div className='col-md-6'>
              <label className='form-label required'>Valor neto</label>
              <div className='input-group'>
                <span className='input-group-text'>$</span>
                <input
                  type='number'
                  className='form-control'
                  name='valor'
                  value={formData.valor}
                  onChange={handleChange}
                  min='0'
                  step='0.01'
                  required
                />
              </div>
            </div>
          </div>

          <div className='row mb-5'>
            <div className='col-md-8'>
              <label className='form-label required'>Descripción</label>
              <textarea
                className='form-control'
                name='descripcion'
                value={formData.descripcion}
                onChange={handleChange}
                rows={3}
                required
              />
            </div>
            <div className='col-md-4'>
              <label className='form-label'>Código Contable</label>
              <input
                type='text'
                className='form-control'
                name='codigo_contable'
                value={formData.codigo_contable}
                onChange={handleChange}
                placeholder='Ej: 4.1.01.001'
              />
              <div className='form-text text-muted'>Código contable asociado al concepto</div>
            </div>
          </div>

          {/* Toggles de configuración */}
          <div className='row mb-5'>
            <div className='col-12'>
              <label className='form-label fw-bold mb-3'>
                <KTIcon iconName='setting-2' className='fs-3 me-2' />
                Configuración del Concepto
              </label>
              <div className='d-flex flex-column gap-4 p-4 bg-light rounded'>
                <div className='form-check form-switch form-check-custom form-check-solid'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='recurrente'
                    name='recurrente'
                    checked={formData.recurrente}
                    onChange={handleChange}
                  />
                  <label className='form-check-label fw-semibold' htmlFor='recurrente'>
                    <div className='d-flex align-items-center'>
                      <KTIcon iconName='timer' className='fs-2 text-primary me-2' />
                      <div>
                        <div className='text-gray-800'>Concepto Recurrente</div>
                        <div className='text-muted fs-7'>Se aplicará automáticamente en cada ciclo de facturación</div>
                      </div>
                    </div>
                  </label>
                </div>

                <div className='separator separator-dashed'></div>

                <div className='form-check form-switch form-check-custom form-check-solid'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='variable'
                    name='variable'
                    checked={formData.variable}
                    onChange={handleChange}
                  />
                  <label className='form-check-label fw-semibold' htmlFor='variable'>
                    <div className='d-flex align-items-center'>
                      <KTIcon iconName='chart-line-up' className='fs-2 text-warning me-2' />
                      <div>
                        <div className='text-gray-800'>Concepto Variable</div>
                        <div className='text-muted fs-7'>El valor puede variar en cada aplicación</div>
                      </div>
                    </div>
                  </label>
                </div>

                <div className='separator separator-dashed'></div>

                <div className='form-check form-switch form-check-custom form-check-solid'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='exonerado'
                    name='exonerado'
                    checked={formData.exonerado}
                    onChange={handleChange}
                  />
                  <label className='form-check-label fw-semibold' htmlFor='exonerado'>
                    <div className='d-flex align-items-center'>
                      <KTIcon iconName='shield-tick' className='fs-2 text-info me-2' />
                      <div>
                        <div className='text-gray-800'>Exonerado de IVA</div>
                        <div className='text-muted fs-7'>El concepto no genera IVA.</div>
                      </div>
                    </div>
                  </label>
                </div>

                <div className='separator separator-dashed'></div>

                <div className='form-check form-switch form-check-custom form-check-solid'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='enviar_facturacion_electronica'
                    name='enviar_facturacion_electronica'
                    checked={formData.enviar_facturacion_electronica}
                    onChange={handleChange}
                  />
                  <label className='form-check-label fw-semibold' htmlFor='enviar_facturacion_electronica'>
                    <div className='text-gray-800'>Enviar a facturación electrónica</div>
                    <div className='text-muted fs-7'>Incluye este concepto en el documento electrónico cuando se emita.</div>
                  </label>
                </div>

                <div className='separator separator-dashed'></div>

                <div className='form-check form-switch form-check-custom form-check-solid'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='estado'
                    name='estado'
                    checked={formData.estado}
                    onChange={handleChange}
                  />
                  <label className='form-check-label fw-semibold' htmlFor='estado'>
                    <div className='d-flex align-items-center'>
                      <KTIcon iconName='verify' className='fs-2 text-success me-2' />
                      <div>
                        <div className='text-gray-800'>Concepto Activo</div>
                        <div className='text-muted fs-7'>El concepto está habilitado y disponible para usar</div>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className='d-flex justify-content-end'>
            <button type='submit' className='btn btn-primary' disabled={loading}>
              {loading ? (
                <>
                  <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                  {isEditMode ? 'Actualizando...' : 'Creando...'}
                </>
              ) : (
                <>
                  <KTIcon iconName={isEditMode ? 'check' : 'plus'} className='fs-3 me-1' />
                  {isEditMode ? 'Actualizar Servicio' : 'Crear Servicio'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
